import React, { useEffect, useState, useMemo } from 'react';
import { View } from 'react-native';
import Duration from 'LocalGluons/components/Duration';
import { addLeadingZero, getTimeStringFromSeconds } from 'Utils/index';
import { PlayerManager } from '@24i/react-native-player';
import type { PlayerData, StreamType, StyleMap, UnixTimeMs } from 'Types';
import Theme from 'Theme';
import { ColorHex } from 'react-native-countdown-circle-timer';
import { ProgramModel, modelType } from '@24i/st-api-connector';
import { useStore } from 'Store';
import { Timeline } from '../TimelineWeb';
import pubSub from '../../../../../pubSub';
import { ACTION } from '../../../../../pubSub/actions';
import defaultStyles, { timelineStyles } from './styles';
import { AdSkipPolicy } from '../../../PlayerContainer/hooks/useAdvertisements';

const getPlayerTimeString = (timestamp: UnixTimeMs, addZeroHours = false) => {
    if (timestamp === 0) return '00:00';

    let timeString = '';
    const time = new Date(timestamp);
    const hours = time.getHours();

    if (addZeroHours && hours === 0) {
        timeString = '00:';
    }
    if (hours >= 1) {
        timeString += `${addLeadingZero(hours)}:`;
    }

    timeString += `${addLeadingZero(time.getMinutes())}`;

    return timeString;
};

interface ProgressBarProps {
    playerInstance: PlayerManager;
    streamType: StreamType;
    style: StyleMap;
    onDurationPress: () => void;
    onCurrentTimePress: () => void;
    testID: string;
    maxTimeshift?: number;
    item: PlayerData['item'];
    onSeekerDragStart: () => void;
    onSeekerDragEnd: (progress: number) => void;
    progressReplace?: any;
    adSkipPolicy: AdSkipPolicy;
    onSyntheticSeekerDragEnd: () => void;
}

const subscriberId = 'player-progress-bar';

const ProgressBar = ({
    playerInstance,
    style,
    onDurationPress,
    onCurrentTimePress,
    streamType,
    testID,
    maxTimeshift,
    item,
    onSeekerDragStart,
    onSeekerDragEnd,
    progressReplace = undefined,
    adSkipPolicy,
    onSyntheticSeekerDragEnd
}: ProgressBarProps): JSX.Element => {
    const [duration, setDuration] = useState(0);
    const { progressInfo } = useStore();
    const { canSkipAd, currentAdBreak } = playerInstance || {};
    const [adIsSkippable, setAdIsSkippable] = useState(playerInstance?.canSkipAd);
    const [isSyntheticChannel, setIsSyntheticChannel] = useState(false);
    const { isGracePeriod } = useStore();
    useEffect(() => {
        setAdIsSkippable(playerInstance?.canSkipAd);
    }, [playerInstance?.canSkipAd]);
    useEffect(() => {
        pubSub.subscribe(
            ACTION.ON_VIDEO_PROGRESS,
            ({ duration: newDuration }) => {
                setDuration(newDuration);
            },
            subscriberId
        );

        return () => {
            pubSub.unsubscribe(subscriberId);
        };
    }, []);

    const showProgressBar = Boolean(playerInstance); // && playerInstance.state.timeWhenLoaded;

    const programRange =
        'getStartTime' in item && 'getEndTime' in item
            ? { start: item.getStartTime(), end: item.getEndTime() }
            : null;

    const {
        adSkippable,
        adUnskippable,
        noRestriction
    }: { adSkippable: ColorHex; adUnskippable: ColorHex; noRestriction: ColorHex } =
        Theme.timelineColor;

    // eslint-disable-next-line no-nested-ternary
    const pointerColor =
        currentAdBreak && !isGracePeriod
            ? adIsSkippable && adSkipPolicy !== AdSkipPolicy.NOT_SKIPPABLE
                ? adSkippable
                : adUnskippable
            : noRestriction;
    const isLiveOrDynArchive = useMemo(
        () =>
            streamType === 'LIVE' ||
            // If there is remaining time (not null/0 value) it is dynamic archive
            Boolean(item.getMetaData().getRemainingTime(new Date().getTime())) ||
            item.modelType === modelType.tv,
        [item, streamType]
    );

    const isLiveAsset = useMemo(
        () =>
            streamType === 'LIVE' ||
            // If there is remaining time (not null/0 value) it is dynamic archive
            Boolean(item.getMetaData().getRemainingTime(new Date().getTime())),
        [item, streamType]
    );

    const getDurationString = (playerTime: UnixTimeMs, assetTime?: UnixTimeMs): string => {
        if (isLiveOrDynArchive && assetTime) {
            return getPlayerTimeString(assetTime, true);
        }
        if (playerTime) return getTimeStringFromSeconds(playerTime / 1000);

        return '00:00';
    };

    // decistion if use synthetic setting in patch ot not. This is necessary because in classic live mode timeline was not colored properly
    useEffect(() => {
        if (item.modelType === 'tv') {
            if ((item as ProgramModel).getChannelType() === 'VOD') {
                setIsSyntheticChannel(true);
            } else {
                setIsSyntheticChannel(false);
            }
        }
    }, [playerInstance?.live]);

    const liveWindowColor = playerInstance?.live || progressReplace || isSyntheticChannel;

    return (
        <View
            testID={testID}
            style={[defaultStyles.container, style]}
            onTouchStart={onSeekerDragStart}
            onTouchEnd={() => {
                if (isSyntheticChannel) onSyntheticSeekerDragEnd();
            }}
        >
            {showProgressBar && (
                <>
                    <Duration
                        key="currentTime"
                        style={defaultStyles.duration}
                        value={getDurationString(playerInstance.currentTime, programRange?.start)}
                        onPress={onCurrentTimePress}
                    />
                    <Timeline
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        forceLiveTime={isLiveOrDynArchive}
                        shouldShowProgressTime
                        key="timeline"
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        playerInstance={playerInstance}
                        programRange={programRange}
                        dvrWindowOverride={programRange?.start}
                        progressReplace={progressReplace || false}
                        styles={{
                            ...timelineStyles,
                            ...(!isLiveOrDynArchive
                                ? {
                                      backgroundContainer: {
                                          backgroundColor: Theme.primaryTextColor
                                      }
                                  }
                                : {}),
                            progressDot: {
                                borderRadius: '3px',
                                backgroundColor: pointerColor
                            },
                            backgroundContainer: {
                                backgroundColor: liveWindowColor
                                    ? Theme.timelineColor.backgroundInactive
                                    : Theme.timelineColor.background
                            },
                            progressTime: {
                                backgroundContainer: {
                                    backgroundColor: Theme.timeProgressColor.primaryBackgroundColor,
                                    borderRadius: 4,
                                    top: -10
                                },
                                time: {
                                    fontFamily: Theme.regularFont,
                                    fontSize: 23,
                                    padding: 5,
                                    overflow: 'hidden'
                                }
                            },
                            midrollsContainer: {
                                borderRadius: 4,
                                position: 'relative',
                                width: '100%',
                                overflow: 'hidden'
                            }
                        }}
                        seekTo={(a) => onSeekerDragEnd(a)}
                        progressInfo={progressInfo}
                        isSyntheticChannel={isSyntheticChannel}
                        // showBuffered
                    />
                    <Duration
                        key="duration"
                        style={defaultStyles.duration}
                        value={getDurationString(playerInstance.duration, programRange?.end)}
                        onPress={onDurationPress}
                    />
                </>
            )}
        </View>
    );
};

export default ProgressBar;
